import React from 'react';
import { linkify } from '../shared/linkify';
import FileChip from './FileChip';

/**
 * MessageBubble — one message in the conversation rail (Epic 7 · S7.1, Part A.5).
 *
 * Presentational. The owning MessageList / ConversationPanel passes the message
 * object; this component never imports the data layer (Epic 1).
 *
 * Shape (mock + live — the mock uses `mine`/`when`/`from`; live may also carry
 * the spec's richer keys, all coerced here):
 *   - is_mine | mine      → right + brand; otherwise left + neutral
 *   - from | sender       → shown when not mine
 *   - from_kind           → 'agent' shows the “AI assistant” marker
 *   - at_label | when     → timestamp label
 *   - body                → verbatim, line breaks preserved (whitespace-pre-wrap),
 *                           links via shared/linkify — NEVER as HTML
 *   - files[]             → FileChips
 *   - about               → chip that opens the related item (onOpenItem)
 *
 * `onFetchUrl` is passed through to FileChip for testability.
 */
const BRAND = '#32B1FF';

const MessageBubble = ({ msg, onOpenItem, onFetchUrl }) => {
  const mine = !!(msg?.is_mine ?? msg?.mine);
  const from = msg?.from || msg?.sender || null;
  const isAgent = msg?.from_kind === 'agent';
  const at = msg?.at_label ?? msg?.when;
  const files = Array.isArray(msg?.files) ? msg.files : [];

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid="message-bubble" data-mine={mine ? 'true' : undefined} data-message-id={msg?.id}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 ${
          mine
            ? 'bg-[#32B1FF] text-white rounded-br-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
        }`}
      >
        {!mine && (from || isAgent) && (
          <div className="flex items-center gap-1.5 mb-0.5">
            {from && <span className="text-xs font-medium opacity-80">{from}</span>}
            {isAgent && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200" data-testid="agent-marker">
                AI assistant
              </span>
            )}
          </div>
        )}
        {msg?.body && (
          <div className="text-sm whitespace-pre-wrap break-words">{linkify(msg.body)}</div>
        )}
        {files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <FileChip key={f?.path || f?.name || i} file={f} onFetchUrl={onFetchUrl} />
            ))}
          </div>
        )}
        {msg?.about && (
          <button
            type="button"
            onClick={() => onOpenItem?.(msg.about)}
            data-testid="about-chip"
            className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              mine ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            ↗ {msg.about.title || 'Open item'}
          </button>
        )}
        {at && (
          <div className={`text-xs mt-1 ${mine ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>{at}</div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
